/**
 * @fileoverview `<Popover>` + `<PopoverContent>` — declarative popover
 * primitives.
 *
 * `<Popover>` is a controlled wrapper that takes `open` and
 * `onOpenChange`. Its children must be a trigger element followed by a
 * `<PopoverContent>` element. `<Popover>` provides shared context
 * (refs, prop spreaders, modal flag, title id) to the content.
 *
 * `<PopoverContent>` reads from that context, renders the floating
 * element when open, and (in modal mode) wraps it in floating-ui's
 * `FloatingFocusManager` for the focus trap + return-focus behavior.
 *
 * @module @puffin-core/popover/Popover
 */

import {
  Children,
  cloneElement,
  createContext,
  useContext,
  useId,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { FloatingFocusManager } from '@puffin-core/floating-ui-base';
import { usePopover } from './usePopover.js';

const PopoverContext = createContext(null);

const DEFAULT_CONTENT_CLASS = 'popover';

/**
 * Compose two refs into one. Forwards the captured DOM node to both
 * the consumer-supplied ref (callback or RefObject) and our floating-ui
 * `setReference` callback.
 */
function composeRefs(consumerRef, floatingRef) {
  return (node) => {
    if (typeof consumerRef === 'function') {
      consumerRef(node);
    } else if (consumerRef && typeof consumerRef === 'object') {
      consumerRef.current = node;
    }
    floatingRef(node);
  };
}

/**
 * Compose two event handlers into one. Used to merge floating-ui's
 * intrinsic trigger handlers (which may or may not be present depending on
 * the registered interactions) with the consumer's own handler so neither
 * is dropped and neither double-fires.
 *
 * Exported so the four composition cases (theirs-only, ours-only, both,
 * neither) can be exercised directly — the controlled popover's call sites
 * never emit an intrinsic `onClick`/`onMouseDown`, so the both/ours-only
 * arms are not reachable through `<Popover>` alone.
 *
 * @param {Function|undefined} theirs - The intrinsic (floating-ui) handler.
 * @param {Function|undefined} ours - The consumer's handler.
 * @returns {Function|undefined} A single composed handler, or undefined when
 *   both inputs are falsy.
 */
export function composeHandlers(theirs, ours) {
  if (!theirs) return ours;
  if (!ours) return theirs;
  return (...args) => {
    theirs(...args);
    ours(...args);
  };
}

/**
 * A `<Popover>` child as the composition reads it: the trigger's ref and the two handlers it
 * composes.
 *
 * @typedef {React.ReactElement<{
 *   ref?: React.Ref<Element>,
 *   onClick?: React.MouseEventHandler,
 *   onMouseDown?: React.MouseEventHandler,
 * }>} PopoverChildElement
 */

/**
 * Declarative popover root. Provides context to PopoverContent.
 *
 * @param {Object} props
 * @param {boolean} props.open - Controlled open state.
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {boolean} [props.modal=false]
 * @param {string} [props.placement='bottom-start']
 * @param {('absolute'|'fixed')} [props.strategy='absolute'] - Positioning
 *   strategy forwarded to `usePopover`. Pass `'fixed'` for an in-flow
 *   popover anchored inside an `overflow`-clipping ancestor (DRP-001).
 * @param {boolean} [props.fitViewport=true] - Cap the popover to the height
 *   available before the viewport edge and scroll internally (forwarded to
 *   `usePopover` → floating-ui `size()` → `--available-height`). Default ON;
 *   pass `false` to fall back to the static `85vh` CSS cap.
 * @param {boolean} [props.dismissOnEscape=true]
 * @param {boolean} [props.dismissOnOutsideClick=true]
 * @param {boolean} [props.enableClick=true] - The popover owns its open-on-click
 *   toggle. Pass `false` for a consumer that drives the open state through its
 *   own trigger handler (controlled bells / menus) so the toggle never double-fires.
 * @param {Object|null} [props.listNavigation=null] - Opt IN to in-floating
 *   roving keyboard navigation (Arrow / Home / End). Default OFF. Forwarded to
 *   `usePopover`. When supplied, render the menu/listbox items via the
 *   render-prop child of `<PopoverContent>` (it receives `{ itemProps }`) and
 *   wire each item's `ref` into the descriptor's `listRef.current[index]`.
 * @param {boolean} [props.returnFocusOnClose=false] - Opt IN to returning focus
 *   to the trigger when a NON-modal popover closes. Default OFF. Forwarded to
 *   `usePopover`.
 * @param {('menu'|'listbox'|'tree'|'grid'|'dialog')} [props.triggerHasPopup] -
 *   Overrides the trigger's `aria-haspopup` kind. Defaults to `'dialog'` (modal)
 *   or `'menu'` (non-modal). A listbox selector should pass `'listbox'`.
 * @param {{current: Element|null}} [props.anchorRef] - ANCHOR mode (PLN-DEPT-GRAIN-004 1b,
 *   owner ruling R-DS-4): position against an element the consumer already renders — a row
 *   kebab's trigger, say — instead of cloning a trigger child. `children` is then the ONE
 *   `<PopoverContent>`; the consumer drives `open` itself (the factory's click toggle is forced
 *   off, there is no trigger to click), and `returnFocusOnClose` returns focus to the anchor.
 *   The anchor is read when the popover OPENS (`anchorRef.current` at that moment), so a ref
 *   that fills in after mount is fine.
 * @param {[React.ReactElement, React.ReactElement]|React.ReactElement} props.children -
 *   Two children: the trigger (forwarded ref + spread props), then a
 *   `<PopoverContent>` — or, in anchor mode, the `<PopoverContent>` alone.
 * @returns {React.JSX.Element}
 */
export function Popover({
  open,
  onOpenChange,
  modal = false,
  placement,
  strategy,
  fitViewport,
  dismissOnEscape,
  dismissOnOutsideClick,
  enableClick = true,
  listNavigation = null,
  returnFocusOnClose = false,
  triggerHasPopup,
  anchorRef = null,
  children,
}) {
  const [titleId, setTitleId] = useState(null);
  const anchored = anchorRef !== null;
  const popover = usePopover({
    open,
    onOpenChange,
    modal,
    placement,
    strategy,
    // undefined → usePopover's `fitViewport = true` default applies (opt-out only).
    fitViewport,
    dismissOnEscape,
    dismissOnOutsideClick,
    // Anchor mode has no trigger element of its own to toggle on: the consumer drives `open`.
    enableClick: anchored ? false : enableClick,
    listNavigation,
    returnFocusOnClose,
    triggerHasPopup,
    titleId,
  });

  // Anchor mode: bind floating-ui's reference to the consumer's element each time the popover
  // opens. Read at open — not at mount — because the anchor is typically a sibling the consumer
  // mounts in the same render (a ref object is empty until React commits it).
  const { setReference } = popover.refs;
  useEffect(() => {
    if (!anchored || !open) return;
    setReference(anchorRef.current);
  }, [anchored, open, anchorRef, setReference]);

  const childrenArray = Children.toArray(children);
  if (anchored) {
    const [contentOnly] = childrenArray;
    return (
      <PopoverContext.Provider
        value={{
          isOpen: popover.isOpen,
          modal,
          refs: popover.refs,
          floatingStyles: popover.floatingStyles,
          context: popover.context,
          contentProps: popover.contentProps,
          itemProps: popover.itemProps,
          close: popover.close,
          registerTitleId: setTitleId,
        }}
      >
        {contentOnly}
      </PopoverContext.Provider>
    );
  }
  // The two children are elements by contract (`props.children`).
  const [triggerChild, contentChild] = /** @type {PopoverChildElement[]} */ (childrenArray);

  // Do NOT pre-merge the consumer's onClick into `triggerProps()`'s
  // input — floating-ui's `getReferenceProps` would echo it on the
  // output, and composing again at clone time would double-fire the
  // handler (a classic toggle bug with `setOpen(v => !v)`). Instead,
  // compose precisely once below: floating-ui's intrinsic handlers
  // first, then the consumer's onClick last.
  const triggerHookProps = popover.triggerProps();

  // React 19: a trigger's ref lives in `props.ref` (ref is a regular prop now).
  // Reading the legacy `triggerChild.ref` getter is deprecated + warns.
  const composedRef = composeRefs(triggerChild.props.ref, popover.refs.setReference);
  const composedTrigger = cloneElement(triggerChild, {
    ...triggerHookProps,
    ref: composedRef,
    onClick: composeHandlers(triggerHookProps.onClick, triggerChild.props.onClick),
    onMouseDown: composeHandlers(triggerHookProps.onMouseDown, triggerChild.props.onMouseDown),
  });

  return (
    <PopoverContext.Provider
      value={{
        isOpen: popover.isOpen,
        modal,
        refs: popover.refs,
        floatingStyles: popover.floatingStyles,
        context: popover.context,
        contentProps: popover.contentProps,
        itemProps: popover.itemProps,
        close: popover.close,
        registerTitleId: setTitleId,
      }}
    >
      {composedTrigger}
      {contentChild}
    </PopoverContext.Provider>
  );
}

Popover.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  modal: PropTypes.bool,
  placement: PropTypes.string,
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  fitViewport: PropTypes.bool,
  dismissOnEscape: PropTypes.bool,
  dismissOnOutsideClick: PropTypes.bool,
  enableClick: PropTypes.bool,
  listNavigation: PropTypes.shape({
    listRef: PropTypes.shape({ current: PropTypes.array }).isRequired,
    activeIndex: PropTypes.number,
    onNavigate: PropTypes.func.isRequired,
    loop: PropTypes.bool,
    focusItemOnOpen: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['auto'])]),
    virtual: PropTypes.bool,
    selectedIndex: PropTypes.number,
    nested: PropTypes.bool,
    orientation: PropTypes.oneOf(['vertical', 'horizontal', 'both']),
    enabled: PropTypes.bool,
  }),
  returnFocusOnClose: PropTypes.bool,
  triggerHasPopup: PropTypes.oneOf(['menu', 'listbox', 'tree', 'grid', 'dialog']),
  /** Anchor mode: a ref to the element the popover positions against; `children` is the content alone. */
  anchorRef: PropTypes.shape({ current: PropTypes.any }),
  children: PropTypes.node.isRequired,
};

/**
 * What a render-prop child of `<PopoverContent>` receives (`usePopover`'s own two members).
 *
 * @typedef {object} PopoverContentApi
 * @property {(index: number, extra?: Object) => Object} itemProps - The per-item spreader (roving
 *   navigation)
 * @property {() => void} close - The item-pick seam: close BEFORE running a picked item's
 *   action
 */

/**
 * Floating popover content. Reads the open/refs/styles from
 * `PopoverContext` (provided by `<Popover>`). When `modal` is true,
 * wraps the content in `FloatingFocusManager` so focus traps + returns
 * to the trigger on close.
 *
 * @param {Object} props
 * @param {React.ReactNode|((api: PopoverContentApi) => React.ReactNode)}
 *   props.children - The popover body. When `listNavigation` is enabled on the
 *   parent `<Popover>`, pass a render-prop function: it receives
 *   `{ itemProps }` (the per-item spreader from `usePopover`) so each
 *   roving-navigable item can be wired without reaching into context. Plain
 *   nodes are still supported for the (common) non-navigable case. The api
 *   also carries `close()` — the ITEM-PICK seam: a menu closed from a picked
 *   item calls it BEFORE running the item's action, so (with
 *   `returnFocusOnClose`) focus is on the trigger while the action runs.
 * @param {string} [props.title] - Optional title rendered as a
 *   focusable `<h2>` at the top. When provided, the popover wires
 *   `aria-labelledby` to its id.
 * @param {string} [props.ariaLabel] - Fallback aria-label when no
 *   `title` is provided. Required for modal popovers without a title.
 * @param {string} [props.className] - Additional class to compose with
 *   the canonical `.popover` class.
 * @param {string} [props.testId='popover-content']
 * @returns {React.JSX.Element|null}
 */
export function PopoverContent({
  children,
  title,
  ariaLabel,
  className,
  testId = 'popover-content',
}) {
  const ctx = useContext(PopoverContext);
  if (!ctx) {
    throw new Error('<PopoverContent> must be rendered inside a <Popover>.');
  }
  const titleId = useId();
  const { registerTitleId } = ctx;

  // Register/unregister the title id with the parent so the parent's
  // usePopover hook can wire `aria-labelledby` on the content element.
  useEffect(() => {
    if (title) {
      registerTitleId(titleId);
      return () => {
        registerTitleId(null);
      };
    }
    return undefined;
  }, [title, titleId, registerTitleId]);

  if (!ctx.isOpen) return null;

  const composedClass = className ? `${DEFAULT_CONTENT_CLASS} ${className}` : DEFAULT_CONTENT_CLASS;

  const baseContentProps = ctx.contentProps({
    className: composedClass,
    'data-testid': testId,
    ...(!title && ariaLabel ? { 'aria-label': ariaLabel } : {}),
  });

  // Render-prop support: when the parent <Popover> has `listNavigation`
  // enabled, the consumer passes a function child so it can spread the
  // per-item `itemProps(index)` onto each roving-navigable item. Plain-node
  // children (the common case) are rendered as-is. `close` is the item-pick seam: call it
  // BEFORE running a picked item's action (see `usePopover`).
  const body =
    typeof children === 'function'
      ? children({ itemProps: ctx.itemProps, close: ctx.close })
      : children;

  const inner = (
    <div ref={ctx.refs.setFloating} style={ctx.floatingStyles} {...baseContentProps}>
      {title ? <h2 id={titleId}>{title}</h2> : null}
      {body}
    </div>
  );

  if (ctx.modal) {
    return (
      <FloatingFocusManager context={ctx.context} modal initialFocus={0} returnFocus>
        {inner}
      </FloatingFocusManager>
    );
  }
  return inner;
}

PopoverContent.propTypes = {
  // Node OR a render-prop function `({ itemProps }) => ReactNode` (used when
  // the parent <Popover> enables `listNavigation`).
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  testId: PropTypes.string,
};
